import { useCallback } from 'react';
import { useServices } from '@/app/services';
import { tokensToSession, userFromProto } from '@/api/session';
import type { TokenPair } from '@/gen/tagn/v1/auth_pb';
import type { User } from '@/gen/tagn/v1/common_pb';

const USER_KEY = 'userId';

/** Login, registration and logout including local data handling. */
export function useAuth() {
  const { api, sessions, db, sync } = useServices();

  const establish = useCallback(
    async (user: User | undefined, tokens: TokenPair | undefined) => {
      if (!tokens) throw new Error('missing tokens');
      const u = userFromProto(user);
      // Another account used this device before: do not mix local data.
      const previous = await db.getMeta(USER_KEY);
      if (previous && previous !== u.id) {
        await db.wipe();
        await sync.reset();
      }
      await db.setMeta(USER_KEY, u.id);
      sessions.set({ user: u, ...tokensToSession(tokens) });
    },
    [db, sessions, sync],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await api.auth.login({ email, password });
      await establish(res.user, res.tokens);
    },
    [api, establish],
  );

  const register = useCallback(
    async (invitationToken: string, displayName: string, password: string) => {
      const res = await api.auth.register({ invitationToken, displayName, password });
      await establish(res.user, res.tokens);
    },
    [api, establish],
  );

  const logout = useCallback(async () => {
    const session = sessions.get();
    try {
      if (session) await api.auth.logout({ refreshToken: session.refreshToken });
    } catch {
      // offline or already expired – the local logout still happens
    }
    sessions.set(null);
    await db.wipe();
    await sync.reset();
  }, [api, sessions, db, sync]);

  return { login, register, logout };
}
